/**
 * Table Threshold component
 * Modified on 16-06-2025
 */
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';

//  Motif imports
import { MotifFormField, MotifInput, MotifLabel, MotifTooltip } from "@ey-xd/motif-react";

//  Icons imports
import iconHelp from '../../../../assets/images/help-circle.svg';

//  Redux imports
import { setSelectedTable, setTables } from "../../../../redux/tableSlice";

//  Internal imports
import "./TableThreshold.scss";
import GenericModal from "../GenericModal";
import ThresholdService from "../../../../services/thresholdService";
import TableService from '../../../../services/tableService';

export default function TableThreshold(props) {
    
    const { onClose, receiveThresholdUpdateStatus } = props;
    const { activeProject } = useSelector(state => state.user);
    const { selectedTable } = useSelector(state => state.table);
    const projectId = activeProject?.project_UUID;
    const [absoluteThresholdValueOld,] = useState(selectedTable.tableAbsoluteThreshold);
    const [absoluteThresholdValue, setAbsoluteThresholdValue] = useState(selectedTable.tableAbsoluteThreshold);
    const [percentageThresholdValueOld,] = useState(selectedTable.tablePercentageThreshold + "%");
    const [percentageThresholdValue, setPercentageThresholdValue] = useState(selectedTable.tablePercentageThreshold + "%");
    const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] = useState(true);
    const dispatch = useDispatch();
    const thresholdService = new ThresholdService();
    const tableService = new TableService();

    
    useEffect(()=>{
        if(
            absoluteThresholdValue && 
            percentageThresholdValue && 
            (
                (parseInt(absoluteThresholdValue) !== parseInt(absoluteThresholdValueOld)) ||
                (percentageThresholdValue !== percentageThresholdValueOld)
            )
        ){
            setIsConfirmButtonDisabled(false);
        }
        else {
            setIsConfirmButtonDisabled(true);
        }
        // eslint-disable-next-line
    }, [ percentageThresholdValue, absoluteThresholdValue])

    const onAbsoluteThresholdInputChange = (input) => {
        const regex = /^[0-9\b]+$/;
        if (input !== "" && regex.test(input)) {
            setAbsoluteThresholdValue(input);
        } else if (input === "") {
            setAbsoluteThresholdValue("");
        } else if (!regex.test(input)) {
            // do nothing
        }
    }

    const onPercentageThresholdInputChange = (input) => {
        const regex = /^[0-9\b]+$/;
        let value = input.replace('%', '');
        if (value === "") {
            setPercentageThresholdValue("");
        }
        else {
            if (regex.test(value) ) {
                setPercentageThresholdValue(value);
            }
        }
    }

    const handleInputBlur = (value) => {
        if (value !== "" && !value.includes("%")) {
            setPercentageThresholdValue(value + '%');
        }
    };

    const handleClose = () => {
        onClose();
    }

    const handleConfirm = async () => {
        const payload = {
            "update_table": [
                [
                    selectedTable.id,
                    parseInt(absoluteThresholdValue, 10),
                    parseInt(percentageThresholdValue.replace('%', ''), 10)
                ]
            ]
        };
        setIsConfirmButtonDisabled(true);
        handleClose();
        const response = await thresholdService.updateGlobalThreshold('Table', payload);
        if (response && response[0] && response[0][0] && (response[0][0] === '200')) {
            receiveThresholdUpdateStatus(response[0][0]);

            const tables = await tableService.getTables(projectId);
            if (tables[0]?.table_details) {
                dispatch(setSelectedTable({
                    selectedTable: tables[0].table_details.filter((table) => table.id === selectedTable.id)[0]
                }));
                dispatch(setTables({
                    tables: tables[0].table_details
                }));
            }
        } else {
            receiveThresholdUpdateStatus(response[0][0]);
        }
    }

    const getModalBody = () => {
        return (
            <div>
                <MotifLabel>Table name</MotifLabel>
                <MotifLabel>{selectedTable.tableName}</MotifLabel>
                <br></br>
                <div className='lblAbsThreshold'>
                    <MotifLabel>Absolute threshold</MotifLabel>
                </div>
                <div className='fldAbsThreshold'>
                    <MotifFormField className="field-group">
                        <MotifInput
                            className="inputThreshold"
                            onChange={(e) => onAbsoluteThresholdInputChange(e.target.value)}
                            type='text'
                            placeholder="Enter Threshold"
                            value={absoluteThresholdValue}
                            hideClearButton={true}
                        />
                        <MotifTooltip
                            className="threshold-tooltip"
                            trigger={
                                <img src={iconHelp} alt='Help' className='helpTooltipIcon' />
                            }
                            hideCloseButton={true}
                            allowClick={false}
                        >
                            <div className="tooltipAbsThresholdTitle">Absolute Threshold</div>
                            <div className="tooltipAbsThrehsoldBody">
                                The number should be written as a whole value <br />
                                and will apply in absolute terms, e.g. if <br />
                                100,000 is set as the threshold, this will apply <br />
                                to both positive and negative values in a table. <br /><br />
                                The threshold will be set across all periods <br />
                                presented in a table, e.g. if 100,000 is set as <br />
                                the threshold, then any rows in the table where <br />
                                the value in each period is less than 100,000 <br />
                                will be excluded.
                            </div>
                        </MotifTooltip>
                    </MotifFormField>
                </div>
                <div className='lblPercThreshold'>
                    <MotifLabel>Percentage threshold</MotifLabel>
                </div>
                <div className='fldPercThreshold'>
                    <MotifFormField className='field-group'>
                        <MotifInput
                            className="inputThreshold"
                            onChange={(e) => onPercentageThresholdInputChange(e.target.value)}
                            onBlur={(e) => handleInputBlur(e.target.value)}
                            type='text'
                            placeholder="Enter Percentage"
                            value={percentageThresholdValue}
                            hideClearButton={true}
                        />
                            <MotifTooltip
                                trigger={
                                    <img src={iconHelp} alt='Help' 
                                    className='helpTooltipIcon' />
                                }                         
                                allowClick={false}
                            >
                                <div className="tooltipAbsThresholdTitle">Percentage threshold</div>
                                <div className='tooltipAbsThrehsoldBody'>
                                The value may reflect an increase or <br />
                                decrease in value presentation.
                                </div>
                            </MotifTooltip>
                    </MotifFormField>
                </div>
            </div>
        )
    }

    return (
        <GenericModal
            modalProperties={{
                show: true,
                className: "genericModal modalMotifFooterModal",
                modalType: "basic",
                title: "Thresholds",
                body: getModalBody(),
                customCloseButtonTitle: "Close",
                rightButtonVariant: "primary",
                leftButtonVariant: "secondary",
                rightButtonSize: "medium",
                leftButtonSize: "medium",
                leftButtonText: "Cancel",
                rightButtonText: "Confirm",
                footerCssClass: "modal-buttons",
                closeHandler: handleClose,
                leftButtonHandler: handleClose,
                rightButtonHandler: handleConfirm,
                rightButtonDisabled: isConfirmButtonDisabled
            }}
        >
        </GenericModal>
    )

} 